import React, { useState, useEffect, useRef } from 'react';
import {
  Form, Select, DatePicker, Input, Button,
  Typography, Row, Col, Card, Spin, notification, Popconfirm, Modal,
  Space, Dropdown, Tooltip
} from 'antd';
import {
  CheckCircleOutlined, CarOutlined, CloseCircleOutlined, RollbackOutlined,
  ExclamationCircleOutlined, SaveOutlined, FileAddOutlined, FileTextOutlined,
  DownOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import salesService from '../../../services/salesService';
import billingService from '../../../services/billingService';
import clientService from '../../../services/clientService';
import lotService from '../../../services/lotService';
import * as productService from '../../../services/productService';
import ReservationModal from './ReservationModal';
import AssignInventoryModal from './AssignInventoryModal';
import InlineLinesTable from './InlineLinesTable';
import OrderDocumentsPanel from './OrderDocumentsPanel';
import { formatMoney } from './orderFormHelpers';
import './SalesOrderForm.css';

const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

const STATUS_BADGE = {
  BORRADOR: { bg: 'var(--fc-badge-draft-bg)', text: 'var(--fc-badge-draft-text)', label: 'BORRADOR' },
  CONFIRMADA: { bg: 'var(--fc-badge-approved-bg)', text: 'var(--fc-badge-approved-text)', label: 'CONFIRMADA' },
  DESPACHADA: { bg: 'var(--fc-badge-dispatched-bg)', text: 'var(--fc-badge-dispatched-text)', label: 'DESPACHADA' },
  FACTURADA: { bg: 'var(--fc-badge-invoiced-bg)', text: 'var(--fc-badge-invoiced-text)', label: 'FACTURADA' },
  CANCELADA: { bg: 'var(--fc-badge-canceled-bg)', text: 'var(--fc-badge-canceled-text)', label: 'CANCELADA' },
};

const SalesOrderFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [headerForm] = Form.useForm();
  // El Modal.confirm estático no lee el tema del ConfigProvider; el del hook sí.
  const [modal, modalContextHolder] = Modal.useModal();

  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(id || null);
  const [orderData, setOrderData] = useState(null);
  const [clientCurrency, setClientCurrency] = useState('COP');

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningDetail, setAssigningDetail] = useState(null);

  const [reservationDetailId, setReservationDetailId] = useState(null);

  const [invoicing, setInvoicing] = useState(false);

  const [clients, setClients] = useState([]);
  const [clientAddresses, setClientAddresses] = useState([]);
  const [categories, setCategories] = useState([]);

  const [allLots, setAllLots] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  const autoSaved = useRef(!!id);
  const headerUpdateTimer = useRef(null);
  const previousClientIdRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
    if (id) fetchOrder(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refreshAvailableLots = async () => {
    const lotsRes = await lotService.getLots({ estado: 'DISPONIBLE' });
    setAllLots(lotsRes?.data?.data || lotsRes?.data || lotsRes || []);
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [clientsRes, lotsRes, catRes, prodRes] = await Promise.all([
        clientService.getClients({ is_active: 'true' }).catch(() => ({ data: [] })),
        lotService.getLots({ estado: 'DISPONIBLE' }).catch(() => ({ data: [] })),
        salesService.getTransactionCategories().catch(() => ({ data: [] })),
        productService.getProducts({ is_active: 'true' }).catch(() => [])
      ]);
      setClients(clientsRes?.data?.data || clientsRes?.data || []);
      setAllLots(lotsRes?.data?.data || lotsRes?.data || lotsRes || []);

      setCategories(catRes?.data?.data || catRes?.data || catRes || []);
      setAllProducts(prodRes?.data?.data || prodRes?.data || prodRes || []);
    } catch (error) {
      notification.error({ message: 'Error', description: 'No se pudieron cargar los datos auxiliares.' });
    } finally {
      if (!id) setLoading(false);
    }
  };

  const fetchOrder = async (oid) => {
    setLoading(true);
    try {
      const res = await salesService.obtenerOrden(oid);
      const data = res?.data?.data || res?.data || res;
      setOrderData(data);
      setOrderId(data.order_id || data.id || data.sales_order_id);

      headerForm.setFieldsValue({
        client_id: data.client_id,
        delivery_date: data.delivery_date ? dayjs(data.delivery_date.slice(0, 10)) : null,
        transaction_category_id: data.transaction_category_id,
        notes: data.notes,
      });
      previousClientIdRef.current = data.client_id;

      const clientRes = await clientService.getClientById(data.client_id);
      const clientInfo = clientRes?.data?.data || clientRes?.data || clientRes;
      if (clientInfo) {
        if (clientInfo.addresses) {
          setClientAddresses(clientInfo.addresses.filter(a => a.is_active !== false));
        }
        setClientCurrency(clientInfo.currency || 'COP');
      }
      headerForm.setFieldsValue({ client_address_id: data.client_address_id });
    } catch (error) {
      notification.error({ message: 'Error', description: 'No se pudo cargar la orden.' });
      navigate('/sales/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = async (clientId) => {
    headerForm.setFieldsValue({ client_address_id: undefined });
    setClientAddresses([]);
    if (!clientId) return;
    try {
      const res = await clientService.getClientById(clientId);
      const clientInfo = res?.data?.data || res?.data || res;
      if (clientInfo) {
        setClientCurrency(clientInfo.currency || 'COP');
        if (clientInfo.addresses) {
          const addrs = clientInfo.addresses.filter(a => a.is_active !== false);
          setClientAddresses(addrs);
          if (addrs.length > 0) {
            headerForm.setFieldsValue({ client_address_id: addrs[0].address_id });
          }
        }
      }
    } catch (error) {
      notification.error({ message: 'Error', description: 'Error al cargar direcciones.' });
    }
  };

  const handleClientChangeWithConfirm = (newClientId) => {
    const currentClientId = previousClientIdRef.current;

    if (!currentClientId || !newClientId) {
      previousClientIdRef.current = newClientId;
      handleClientChange(newClientId);
      return;
    }

    if (String(currentClientId) === String(newClientId)) {
      return;
    }

    const currentClient = clients.find(c => String(c.client_id) === String(currentClientId));
    const newClient = clients.find(c => String(c.client_id) === String(newClientId));
    const currentName = currentClient?.name || 'el cliente actual';
    const newName = newClient?.name || 'el nuevo cliente';

    Modal.confirm({
      title: 'Cambiar cliente de la orden',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <p>Confirmar cambio:</p>
          <p style={{ margin: '12px 0' }}>
            <strong>De:</strong> {currentName}<br />
            <strong>A:</strong> {newName}
          </p>
          <p style={{ color: 'var(--fc-text-secondary)', fontSize: 13 }}>
            Se actualizará la dirección de entrega y podría afectar precios
            y disponibilidad. ¿Confirmas el cambio?
          </p>
        </div>
      ),
      okText: 'Sí, cambiar cliente',
      cancelText: 'Cancelar',
      onOk: () => {
        previousClientIdRef.current = newClientId;
        handleClientChange(newClientId);
      },
      onCancel: () => {
        headerForm.setFieldsValue({ client_id: currentClientId });
      },
    });
  };

  // Solo las órdenes en BORRADOR son editables. El !!orderData deja pasar la
  // orden nueva (aún sin cargar), que necesita el formulario habilitado para
  // que el autoguardado pueda crearla.
  const isReadOnly = !!orderData && orderData.status !== 'BORRADOR';

  const headerVals = Form.useWatch([], headerForm) || {};
  // El efecto solo necesita saber si ya hay orden cargada; depender del objeto
  // completo lo re-dispararía (y enviaría un PATCH) cada vez que cambian sus líneas.
  const hasOrderData = !!orderData;

  useEffect(() => {
    if (orderId) {
      if (isReadOnly || !hasOrderData) return;
      if (headerUpdateTimer.current) clearTimeout(headerUpdateTimer.current);
      headerUpdateTimer.current = setTimeout(() => {
        if (headerVals.client_id && headerVals.client_address_id && headerVals.delivery_date) {
          const payload = {
            client_id: headerVals.client_id,
            client_address_id: headerVals.client_address_id,
            delivery_date: headerVals.delivery_date.format('YYYY-MM-DD'),
            transaction_category_id: headerVals.transaction_category_id || null,
            notes: headerVals.notes
          };
          salesService.actualizarHeader(orderId, payload).catch(() => { });
        }
      }, 800);
      return () => clearTimeout(headerUpdateTimer.current);
    } else {
      if (autoSaved.current) return;
      if (headerVals.client_id && headerVals.client_address_id && headerVals.delivery_date) {
        autoSaved.current = true;
        const payload = {
          ...headerVals,
          delivery_date: headerVals.delivery_date.format('YYYY-MM-DD')
        };
        salesService.autoGuardarOrden(payload)
          .then(res => {
            const newId = res?.data?.data?.order_id || res?.data?.data?.id || res?.data?.data?.sales_order_id || res?.data?.sales_order_id || res?.data?.order_id;
            setOrderId(newId);
            setOrderData(res?.data || res?.data?.data);
            notification.success({ message: 'Borrador auto-guardado' });
            navigate(`/sales/orders/${newId}`, { replace: true });
          })
          .catch(() => { autoSaved.current = false; });
      }
    }
    // Se listan los campos sueltos a propósito: así el temporizador del
    // autoguardado no se reinicia en renders que no cambian esos campos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerVals.client_id, headerVals.client_address_id, headerVals.delivery_date, headerVals.transaction_category_id, headerVals.notes, orderId, isReadOnly, hasOrderData, navigate]);

  // Al agregar llega la línea creada y se suma al estado sin volver a pedir la
  // orden (los totales se recalculan solos desde details). Editar y eliminar no
  // pasan línea y siguen recargando la orden completa.
  const handleLineAdded = (newLine) => {
    if (newLine) {
      setOrderData(prev => ({ ...prev, details: [...(prev.details || []), newLine] }));
      return;
    }
    fetchOrder(orderId);
    refreshAvailableLots();
  };

  const STATUS_CHANGE_SUCCESS = {
    CONFIRMADA: 'Orden confirmada con éxito',
    DESPACHADA: 'Orden despachada con éxito',
    CANCELADA: 'Orden cancelada',
    BORRADOR: 'Orden devuelta a borrador',
  };

  const handleChangeStatus = async (newStatus) => {
    try {
      setLoading(true);
      await salesService.cambiarEstadoOrden(orderId, newStatus);
      notification.success({ message: STATUS_CHANGE_SUCCESS[newStatus] || 'Estado actualizado' });
      fetchOrder(orderId);
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.mensaje || 'No se pudo actualizar el estado de la orden.' });
      setLoading(false);
    }
  };

  const handleGuardar = async () => {
    try {
      const values = await headerForm.validateFields();
      if (!orderId) {
        notification.info({
          message: 'Los datos generales se guardarán automáticamente al completarlos',
        });
        return;
      }
      const payload = {
        client_id: values.client_id,
        client_address_id: values.client_address_id,
        delivery_date: values.delivery_date.format('YYYY-MM-DD'),
        transaction_category_id: values.transaction_category_id || null,
        notes: values.notes,
      };
      setLoading(true);
      await salesService.actualizarHeader(orderId, payload);
      notification.success({ message: 'Orden guardada' });
      fetchOrder(orderId);
    } catch (error) {
      if (error?.errorFields) {
        notification.error({
          message: 'Faltan campos obligatorios',
          description: 'Revisa los campos marcados en rojo antes de guardar.',
        });
      } else {
        notification.error({
          message: 'Error al guardar',
          description: error?.response?.data?.mensaje || 'Error desconocido',
        });
        setLoading(false);
      }
    }
  };

  const handleDispatchClick = () => {
    modal.confirm({
      title: `Despachar la orden ${orderData.display_number}`,
      icon: <ExclamationCircleOutlined />,
      content: 'Se generará el despacho y se asignará un número de fito. Ese número no se puede reutilizar.',
      okText: 'Despachar',
      cancelText: 'Cancelar',
      onOk: () => handleChangeStatus('DESPACHADA'),
    });
  };

  const handleCancelClick = () => {
    Modal.confirm({
      title: '¿Cancelar esta orden?',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: 'El inventario reservado volverá a quedar disponible.',
      okText: 'Sí, cancelar orden',
      okType: 'danger',
      cancelText: 'Volver',
      onOk: () => handleChangeStatus('CANCELADA'),
    });
  };

  // Desde el menú ⋯ no cabe un Popconfirm: mismos textos en un Modal.confirm.
  const handleRevertConfirmedClick = () => {
    Modal.confirm({
      title: '¿Volver la orden a borrador?',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: 'La orden volverá a ser editable. No afecta el inventario.',
      okText: 'Sí, volver a borrador',
      cancelText: 'Cancelar',
      onOk: () => handleChangeStatus('BORRADOR'),
    });
  };

  // Revertir un despacho sí mueve inventario, así que su confirmación explica
  // qué pasa con lo despachado.
  const handleRevertDispatchClick = () => {
    Modal.confirm({
      title: '¿Devolver la orden a borrador?',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: 'Esta orden ya fue despachada. Al devolverla a borrador, el inventario despachado volverá a quedar reservado para esta orden y la orden será editable de nuevo.',
      okText: 'Sí, volver a borrador',
      cancelText: 'Cancelar',
      onOk: () => handleChangeStatus('BORRADOR'),
    });
  };

  // El PDF no se abre solo: queda en Documentos, ya preseleccionado.
  const handleInvoiceClick = async () => {
    setInvoicing(true);
    try {
      const response = await billingService.crearFactura([orderData.active_dispatch.id]);
      notification.success({
        title: 'Factura emitida',
        description: `Factura ${response?.data?.display_number} creada exitosamente.`,
      });
      // Se espera la recarga antes de soltar el botón, para que no quede
      // pulsable con la orden vieja.
      await fetchOrder(orderId);
    } catch (error) {
      notification.error({
        title: 'No se pudo emitir la factura',
        description: error?.response?.data?.mensaje || 'Error inesperado.',
      });
    } finally {
      setInvoicing(false);
    }
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssigningDetail(null);
  };

  const handleAssignSubmit = async (validRows) => {
    if (!assigningDetail) return;
    try {
      await salesService.asignarInventario(assigningDetail.detail_id, validRows);
      notification.success({ message: 'Inventario asignado exitosamente' });
      closeAssignModal();
      fetchOrder(orderId);
      refreshAvailableLots();
    } catch (error) {
      notification.error({ message: 'Error al asignar', description: error.response?.data?.mensaje || 'No se pudo asignar el inventario.' });
    }
  };

  const handleReleaseAssignment = async (assignmentId) => {
    setLoading(true);
    try {
      await salesService.liberarAsignacion(assignmentId);
      notification.success({ message: 'Asignación liberada' });
      fetchOrder(orderId);
      refreshAvailableLots();
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.mensaje || 'No se pudo liberar la asignación.' });
      setLoading(false);
    }
  };

  const orderLines = orderData?.details || [];
  // Line subtotals already come rounded to 2 decimals from the DB (Prisma Decimal(10,2),
  // serialized as string). Sum the rounded subtotals, then round only the final result
  // to clear floating-point drift (e.g. 93.60000000000001 -> 93.60).
  const totalAmount = Math.round(
    orderLines.reduce((acc, l) => acc + (Number(l.subtotal) || 0), 0) * 100
  ) / 100;
  // El backend ya excluye las líneas borradas y guarda los totales reales de
  // cada línea: un surtido, lo de su caja por el número de cajas.
  const sumTotal = (field) => orderLines.reduce((acc, l) => acc + (Number(l[field]) || 0), 0);
  const orderTotals = [
    ['Total cajas', sumTotal('total_boxes')],
    ['Total ramos', sumTotal('total_bunches')],
    ['Total tallos', sumTotal('total_stems')],
  ];
  const status = orderData?.status;
  const isDraft = status === 'BORRADOR';
  // FACTURADA no es un estado guardado: es una orden DESPACHADA con factura emitida.
  const headerStatus = status === 'DESPACHADA' && orderData?.invoicing_status === 'FACTURADA'
    ? 'FACTURADA'
    : status;
  const invoice = orderData?.invoices?.[0];

  // Los botones del encabezado no aparecen ni desaparecen: cuando no aplican
  // quedan deshabilitados y dicen por qué. Una orden nueva cuenta como borrador.
  const isDraftStep = !headerStatus || headerStatus === 'BORRADOR';

  // Los motivos dicen qué hacer, sin repetir el estado que ya muestra el badge.
  const dispatchBlockedReason = {
    DESPACHADA: 'Ya fue despachada',
    FACTURADA: 'Ya fue despachada',
    CANCELADA: 'La orden está cancelada',
  }[headerStatus] || null;

  const invoiceBlockedReason = (() => {
    if (headerStatus === 'FACTURADA') return 'Ya fue facturada';
    if (headerStatus === 'CANCELADA') return 'La orden está cancelada';
    if (headerStatus !== 'DESPACHADA') return 'Despache primero la orden';
    return orderData.active_dispatch ? null : 'No tiene despacho para facturar';
  })();

  const revertHandler = {
    CONFIRMADA: handleRevertConfirmedClick,
    DESPACHADA: handleRevertDispatchClick,
  }[headerStatus];
  const canCancel = !!orderId && (headerStatus === 'BORRADOR' || headerStatus === 'CONFIRMADA');

  const actions = [
    {
      key: 'revert',
      icon: <RollbackOutlined />,
      label: 'Volver a borrador',
      onSelect: revertHandler,
      reason: revertHandler ? null : {
        FACTURADA: `Anule primero la factura ${invoice?.display_number}`,
        CANCELADA: 'La orden está cancelada',
      }[headerStatus] || 'Ya está en borrador',
    },
    {
      key: 'invoice',
      icon: <FileTextOutlined />,
      label: invoice ? `Ver factura ${invoice.display_number}` : 'Ver factura',
      onSelect: invoice ? () => navigate(`/sales/invoices/${invoice.id}`) : null,
      reason: invoice ? null : 'Aún no tiene factura',
    },
    {
      key: 'cancel',
      icon: <CloseCircleOutlined />,
      label: 'Cancelar orden',
      danger: true,
      onSelect: canCancel ? handleCancelClick : null,
      reason: canCancel ? null : {
        DESPACHADA: 'Vuelva primero a borrador',
        FACTURADA: 'La orden está facturada',
        CANCELADA: 'Ya está cancelada',
      }[headerStatus] || 'Complete primero los datos de la orden',
    },
  ];

  const actionMenuItems = actions.map(({ key, icon, label, danger, onSelect, reason }) => ({
    key,
    icon,
    danger,
    disabled: !onSelect,
    label: reason ? (
      <div>
        <div>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--fc-text-muted)', whiteSpace: 'normal', maxWidth: 260 }}>
          {reason}
        </div>
      </div>
    ) : label,
  }));

  const reservationDetail = reservationDetailId
    ? orderLines.find(r => (r.detail_id || r.id) === reservationDetailId) || null
    : null;
  const closeReservationModal = () => setReservationDetailId(null);

  return (
    <Spin spinning={loading}>
      <div style={{ padding: '24px' }}>

        <div style={{ marginBottom: 24 }}>
          <div style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 1.2,
            color: 'var(--fc-text-muted)',
            marginBottom: 8,
          }}>
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/sales/orders')}
            >ÓRDENES DE VENTA</span>
            <span style={{ margin: '0 8px' }}>/</span>
            <span>{orderData ? 'EDITAR' : 'NUEVA'}</span>
          </div>

          <Row justify="space-between" align="middle" wrap style={{ rowGap: 12 }}>
            <Col>
              <Title level={2} style={{
                margin: 0,
                fontWeight: 600,
                color: 'var(--fc-text-primary)',
                fontSize: 28,
                display: 'inline-block',
                marginRight: 12,
              }}>
                {orderData ? `Orden ${orderData.display_number}` : 'Nueva Orden'}
              </Title>
              {headerStatus && STATUS_BADGE[headerStatus] && (
                <span style={{
                  backgroundColor: STATUS_BADGE[headerStatus].bg,
                  color: STATUS_BADGE[headerStatus].text,
                  padding: '4px 12px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  verticalAlign: 'middle',
                }}>
                  {STATUS_BADGE[headerStatus].label}
                </span>
              )}
            </Col>
            <Col>
              {/* wrap: en pantallas angostas los botones bajan en el mismo orden. */}
              <Space size={12} align="center" wrap>
                <Tooltip title={isReadOnly ? 'Vuelva primero a borrador para editarla' : null}>
                  <Button icon={<SaveOutlined />} disabled={isReadOnly} onClick={handleGuardar}>Guardar</Button>
                </Tooltip>
                {isDraftStep ? (
                  <Tooltip title={orderLines.length === 0 ? 'Agregue líneas para confirmar' : null}>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      disabled={orderLines.length === 0}
                      onClick={() => handleChangeStatus('CONFIRMADA')}
                    >Confirmar orden</Button>
                  </Tooltip>
                ) : (
                  <Tooltip title={dispatchBlockedReason}>
                    <Button
                      type={headerStatus === 'CONFIRMADA' ? 'primary' : 'default'}
                      icon={<CarOutlined />}
                      disabled={loading || !!dispatchBlockedReason}
                      onClick={handleDispatchClick}
                    >Despachar</Button>
                  </Tooltip>
                )}
                {/* Misma confirmación que en Facturación: emitir no se deshace.
                    El Tooltip va pegado al botón: a través del Popconfirm no le
                    llega el pointerenter, y en un botón deshabilitado no se abriría. */}
                <Popconfirm
                  title="Emitir factura"
                  description="Se creará 1 factura."
                  okText="Emitir"
                  cancelText="Cancelar"
                  onConfirm={handleInvoiceClick}
                  disabled={!!invoiceBlockedReason}
                >
                  <Tooltip title={invoiceBlockedReason}>
                    <Button
                      type={headerStatus === 'DESPACHADA' ? 'primary' : 'default'}
                      icon={<FileAddOutlined />}
                      loading={invoicing}
                      disabled={loading || !!invoiceBlockedReason}
                    >Facturar</Button>
                  </Tooltip>
                </Popconfirm>
                <OrderDocumentsPanel order={orderData} primary={headerStatus === 'FACTURADA'} />
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  disabled={loading}
                  menu={{
                    items: actionMenuItems,
                    onClick: ({ key }) => actions.find((a) => a.key === key).onSelect(),
                  }}
                >
                  <Button>Acciones <DownOutlined /></Button>
                </Dropdown>
              </Space>
            </Col>
          </Row>
        </div>

        <Row gutter={24} style={{ marginBottom: 0 }}>
          <Col xs={24} md={16}>

            <Card
              title={
                <span style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'var(--fc-text-primary)',
                }}>
                  Detalles de la orden
                </span>
              }
              variant="borderless"
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                border: '1px solid var(--fc-border)',
                backgroundColor: 'var(--fc-surface)',
              }}
              styles={{
                header: {
                  border: 'none',
                  paddingTop: 20,
                  paddingBottom: 4,
                },
                body: {
                  paddingTop: 12,
                },
              }}
            >
              <Form form={headerForm} layout="vertical" disabled={isReadOnly}>
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item name="client_id" label={<span style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      color: 'var(--fc-text-secondary)',
                      fontWeight: 500,
                    }}>Cliente</span>} rules={[{ required: true }]}>
                      <Select showSearch placeholder="Buscar cliente" onChange={handleClientChangeWithConfirm} filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
                        {clients.map(c => <Option key={c.client_id} value={c.client_id}>{c.name}</Option>)}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="client_address_id" label={<span style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      color: 'var(--fc-text-secondary)',
                      fontWeight: 500,
                    }}>Dirección de Entrega</span>} rules={[{ required: true }]}>
                      <Select placeholder="Seleccionar Dirección">
                        {clientAddresses.map(a => <Option key={a.address_id} value={a.address_id}>{a.address_line} ({a.city})</Option>)}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="delivery_date" label={<span style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      color: 'var(--fc-text-secondary)',
                      fontWeight: 500,
                    }}>Fecha de Entrega</span>} rules={[{ required: true }]}>
                      <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item name="transaction_category_id" label={<span style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      color: 'var(--fc-text-secondary)',
                      fontWeight: 500,
                    }}>Categoría (Opcional)</span>}>
                      <Select placeholder="Seleccionar" allowClear>
                        {categories.map(cat => <Option key={cat.id || cat.category_id} value={cat.id || cat.category_id}>{cat.name}</Option>)}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    {/* Sin name: es de solo lectura y no debe viajar en el autoguardado. */}
                    <Form.Item label={<span style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      color: 'var(--fc-text-secondary)',
                      fontWeight: 500,
                    }}>Fito</span>}>
                      {/* Una orden despachada sin fito es de antes del consecutivo. */}
                      <Input
                        value={orderData?.fito_display || ''}
                        placeholder={status === 'DESPACHADA' ? 'Sin consecutivo' : 'Se asigna al despachar'}
                        readOnly
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="notes" label={<span style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      color: 'var(--fc-text-secondary)',
                      fontWeight: 500,
                    }}>Notas</span>}>
                      <TextArea rows={1} placeholder="Opcional..." />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>

          </Col>

          <Col xs={0} md={8}>
            <div style={{
              backgroundColor: 'var(--fc-surface-dark)',
              color: 'var(--fc-text-on-dark)',
              borderRadius: 12,
              padding: 24,
            }}>
              <div style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: 'var(--fc-text-muted)',
                marginBottom: 16,
              }}>Resumen</div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
                fontSize: 14,
              }}>
                <span>Subtotal</span>
                <span>{formatMoney(totalAmount, clientCurrency)}</span>
              </div>

              <div style={{
                height: 1,
                backgroundColor: 'rgba(255,255,255,0.1)',
                margin: '16px 0',
              }} />

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}>
                <span style={{ fontSize: 16, fontWeight: 600 }}>Total</span>
                <span style={{ fontSize: 22, fontWeight: 700 }}>
                  {formatMoney(totalAmount, clientCurrency)}
                </span>
              </div>

              <div style={{ marginTop: 12 }}>
                {orderTotals.map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginTop: 6,
                      fontSize: 15,
                    }}
                  >
                    <span style={{ color: 'var(--fc-text-muted)' }}>{label}</span>
                    <span style={{ fontWeight: 600 }}>{value.toLocaleString('es-CO')}</span>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        <InlineLinesTable
          orderId={orderId}
          orderLines={orderLines}
          allProducts={allProducts}
          clientCurrency={clientCurrency}
          isReadOnly={isReadOnly}
          onLinesChanged={handleLineAdded}
        />

      </div>

      <AssignInventoryModal
        open={assignModalOpen}
        detail={assigningDetail}
        allLots={allLots}
        onClose={closeAssignModal}
        onSubmit={handleAssignSubmit}
        onRelease={handleReleaseAssignment}
      />

      <ReservationModal
        detail={reservationDetail}
        onClose={closeReservationModal}
        isDraft={isDraft}
        onRelease={handleReleaseAssignment}
      />

      {modalContextHolder}
    </Spin>
  );
};

export default SalesOrderFormPage;
